import { useMemo, useState, type MouseEvent, type ReactNode, type Ref } from 'react';
import { Bot, ChevronRight, Copy, Lock } from 'lucide-react';
import { Avatar, Button, IconButton, Typography } from '../ui';
import { Markdown } from '../../Markdown';
import { MaestroMark } from '../MaestroMark';
import { MessageAttachments } from '../contacts/MessageAttachments';
import { OutreachLaneItem } from './OutreachLaneItem';
import { avatarInitials, avatarSlotFor } from '../../../lib/avatar-color';
import { StageDot } from '../contacts/StageDot';
import { strippedMessageText, threadTitle } from '../../../lib/contact-rows';
import type { WebOutreachAttachment, WebOutreachThread } from '../../../lib/contact-book';
import { laneLabel, parseOwnerEventKind, type TranscriptItem } from '../../../lib/outreach-lane';
import { lastAgentDelivery, stageBiValue, threadStage } from '../../../lib/outreach-stage';
import {
  contactTimelineRows,
  threadRows,
  type DayLabel,
  type ThreadEntry,
  type ThreadMessageView,
  type ThreadViewContext,
  type ThreadViewMessage,
  type TimelineThread,
} from '../../../lib/outreach-thread-view';
import { messageClock, messageExactTime, renderDayLabel, renderThreadLabel, renderTopicTitle } from '../../../lib/outreach-thread-view-render';
import { messageDir } from '../../../lib/message-direction';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useMe } from '../../../lib/me';
import { useRecipient } from '../../settings/shared/settings-queries';
import styles from './outreach-thread-transcript.module.css';

/** A message as every thread surface holds it: the model's fields plus what the bubble draws. */
export type TranscriptMessage = ThreadViewMessage & {
  text?: string;
  delivery?: string | null;
  attachments?: WebOutreachAttachment[];
};

type TFn = (key: string, opts?: Record<string, unknown>) => string;

/**
 * The owner's own names, for the speaker model's legacy envelope match (`ThreadViewContext.ownerNames`): a
 * legacy reply envelope names the replier's Recipient, so this is what puts OUR old replies on our side. The
 * Recipient name first (what the envelope carries), then the Wix identity name. The phones
 * (iOS `OutreachThreadOwner.names`, Android `threadOwnerNames`) read the Recipient name, else a display name
 * that is a real name (never the email local part); they hold no identity first/last name, so that source is
 * web's alone.
 */
export function useThreadOwnerNames(): string[] {
  const me = useMe();
  const { data: recipient } = useRecipient();
  const identity = me ? `${me.firstName ?? ''} ${me.lastName ?? ''}`.trim() : '';
  const recipientName = recipient?.name?.trim() ?? '';
  return useMemo(() => [recipientName, identity].filter(Boolean), [recipientName, identity]);
}

/** A touch screen: no hover, so the bubble's Copy is revealed by a tap or a long-press instead. */
function isTouchOnly(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(hover: none)').matches;
}

/**
 * An outreach thread's correspondence, drawn from the shared speaker model (`lib/outreach-thread-view.ts`,
 * contract `native-apps/shared/outreach-thread-view/contract.json`) — the ONE renderer behind the Contacts /
 * Inbox viewer, the full-page thread and the task's thread dialog, so the three cannot drift on who said what.
 *
 * - SIDE: my agent and I sit on the end edge, the contact and their agent on the start edge (mirrors in RTL).
 * - STYLE: a person's words are a solid bubble; an agent's a tinted, outlined one, its label carrying the
 *   agent mark.
 * - LABEL: on the first bubble of each run of one speaker ("{agent} · on your behalf", the contact's name,
 *   "{name}'s agent"); an unknown speaker gets none.
 * - TIME: a day divider between calendar days, a short clock time beside each bubble (exact instant on hover),
 *   never a relative "13w".
 * - The owner's lane stays a centred, muted note that says it is theirs alone. A TOLD note is ONE private row
 *   ("To {agent} · only you see this" over the owner's words); one the agent then sent verbatim draws nothing,
 *   and that bubble is the owner's words ("You · sent by {agent}") — the model's `relayed`. Any lane row ends
 *   a run, so the bubble after it names its speaker again.
 * - Copy is a hover / focus action on the bubble, not an icon under every message; on a touch screen (no
 *   hover) a tap or long-press on the bubble reveals it — native's long-press menu, not an icon in every bubble.
 */
export function OutreachThreadTranscript({
  messages,
  transcript: merged,
  ctx,
  maestroAgentName,
  outreachId,
  page,
  contactPhoto,
  avatarKey,
  endRef,
}: {
  messages: TranscriptMessage[];
  /** `mergeTranscript(messages, ownerEvents, optimistic)`, in display order. */
  transcript: TranscriptItem<TranscriptMessage>[];
  ctx: ThreadViewContext;
  maestroAgentName: string;
  outreachId: string;
  page: string;
  contactPhoto?: string;
  /** The contact's avatar colour key (personId, else their name). */
  avatarKey: string;
  /** Attached to the last row, for opening the thread at its end. */
  endRef?: Ref<HTMLDivElement>;
}) {
  const now = useMemo(() => new Date(), []);
  const ownerNames = useThreadOwnerNames();
  const speakerCtx = useMemo<ThreadViewContext>(() => ({ ...ctx, ownerNames }), [ctx, ownerNames]);
  // A note the lane does not render (`laneLabel` null) is dropped before the model sees it, so it can neither
  // carry a day divider nor open a caption group over nothing.
  const transcript = merged.filter((item) => item.kind !== 'event' || laneLabel(item.event, now) !== null);
  const entries: ThreadEntry[] = transcript.map((item) =>
    item.kind === 'message'
      ? { kind: 'message', index: item.index }
      : { kind: 'note', at: item.event.at, lane: parseOwnerEventKind(item.event.kind), text: item.event.text },
  );
  const rows = threadRows(messages, speakerCtx, now, entries);
  const delivery = lastAgentDelivery(messages);

  return (
    <div className={styles.transcript}>
      {transcript.map((item, position) => {
        const row = rows[position];
        const divider = row?.divider ? <DayDivider day={row.divider} /> : null;
        const isLast = position === transcript.length - 1;

        if (item.kind === 'event' || !row || 'note' in row) {
          if (item.kind !== 'event') return null;
          // A note the next bubble sent verbatim: that bubble ("You · sent by …") says it, so nothing here.
          if (row && 'note' in row && row.relayed) return null;
          const privateNote = Boolean(row && 'note' in row && row.privateNote);
          // The caption once per consecutive group of plain notes: the lane is the owner's alone. A private
          // note carries its own lock and header, so it neither takes the caption nor counts as the group.
          const previous = rows[position - 1];
          const firstOfGroup = !previous || !('note' in previous) || Boolean(previous.privateNote || previous.relayed) || Boolean(row?.divider);
          return (
            <div key={`${item.source}-${item.index}`} ref={isLast ? endRef : undefined} className={styles.noteBlock}>
              {divider}
              {firstOfGroup && !privateNote ? <OwnerOnlyCaption /> : null}
              <OutreachLaneItem event={item.event} outreachId={outreachId} page={page} privateNote={privateNote} />
            </div>
          );
        }

        return (
          <TranscriptBubble
            key={`m-${item.index}`}
            rowRef={isLast ? endRef : undefined}
            divider={divider}
            message={item.message}
            row={row}
            contactName={ctx.contactName}
            maestroAgentName={maestroAgentName}
            deliveryKey={delivery?.index === item.index ? delivery.key : undefined}
            page={page}
            contactPhoto={contactPhoto}
            avatarKey={avatarKey}
          />
        );
      })}
    </div>
  );
}

/** The day between two days of messages — centred and quiet. */
function DayDivider({ day }: { day: DayLabel }) {
  const { t, i18n } = useTranslation();
  return (
    <div className={styles.divider} role="separator">
      <Typography variant="p3" color="secondary">
        {renderDayLabel(day, t as unknown as TFn, i18n.language || undefined)}
      </Typography>
    </div>
  );
}

/**
 * ONE message: the run label (first of a run), the bubble with its clock beside it (and the delivery word on
 * the last agent message), copy on hover / focus (a tap or long-press on touch), the files under it. Shared by
 * the thread transcript and the contact timeline, so a bubble cannot drift between the two.
 *
 * The bubble is a group NAMED by its speaker on every message — a continuation bubble draws no label, but a
 * screen reader still hears who said it (native's bubble label does the same). The text's direction rides an
 * INNER element, so an RTL message does not flip the bubble's own corners (the Copy action stays put).
 */
function TranscriptBubble({
  rowRef,
  divider,
  message,
  row,
  contactName,
  maestroAgentName,
  deliveryKey,
  page,
  contactPhoto,
  avatarKey,
}: {
  rowRef?: Ref<HTMLDivElement>;
  divider: ReactNode;
  message: TranscriptMessage;
  row: ThreadMessageView;
  contactName: string;
  maestroAgentName: string;
  deliveryKey?: string;
  page: string;
  contactPhoto?: string;
  avatarKey: string;
}) {
  const { t, i18n } = useTranslation();
  const tr = t as unknown as TFn;
  const { logPageClickBi } = useGenericBi();
  const locale = i18n.language || undefined;
  const body = strippedMessageText(message.text);
  const mine = row.side === 'mine';
  const agent = row.style === 'agent';
  const speakerName = renderThreadLabel(row.label, tr, maestroAgentName);
  const label = row.showLabel ? speakerName : '';
  const [revealed, setRevealed] = useState(false);
  const clock = messageClock(message.sentAt, locale);
  const exact = messageExactTime(message.sentAt, locale);
  const time = deliveryKey && clock ? `${clock} · ${t(deliveryKey)}` : clock;
  const bubbleClass = [styles.bubble, agent ? styles.bubbleAgent : mine ? styles.bubblePersonMine : styles.bubblePersonTheirs].join(' ');
  // Touch only: a tap (not on a link or button inside) or a long-press reveals Copy; desktop keeps hover/focus.
  const revealOnTap = (event: MouseEvent<HTMLDivElement>) => {
    if (!isTouchOnly() || (event.target as HTMLElement).closest('a, button')) return;
    setRevealed((shown) => !shown);
  };
  // A long-press only REVEALS Copy: it never `preventDefault`s and skips links and buttons, so Android Chrome
  // keeps its own link menu and text selection on the bubble.
  const revealOnLongPress = (event: MouseEvent<HTMLDivElement>) => {
    if (!isTouchOnly() || (event.target as HTMLElement).closest('a, button')) return;
    setRevealed(true);
  };

  return (
    <div ref={rowRef} className={styles.messageBlock}>
      {divider}
      <div className={mine ? `${styles.row} ${styles.rowMine}` : styles.row}>
        {label ? (
          <span className={styles.label}>
            {/* My agent wears the Maestro mark; theirs a generic agent glyph (the mark is reserved). */}
            {agent && mine ? <MaestroMark size={16} /> : null}
            {agent && !mine ? <Bot size={16} aria-hidden className={styles.agentGlyph} /> : null}
            {!agent && !mine ? (
              <Avatar initials={avatarInitials(contactName)} photoUrl={contactPhoto} size={18} color={avatarSlotFor(avatarKey)} />
            ) : null}
            <Typography variant="p3" color="secondary">
              {label}
            </Typography>
          </span>
        ) : null}
        <div className={styles.bubbleLine}>
          {/* Touch reveal only (tap / long-press); a keyboard reaches Copy by focusing it, which shows it. */}
          <div
            className={bubbleClass}
            role="group"
            aria-label={speakerName || undefined}
            data-revealed={revealed ? 'true' : undefined}
            onClick={revealOnTap}
            onContextMenu={revealOnLongPress}
          >
            <div className={styles.bubbleText} dir={messageDir(body)}>
              <Markdown>{body}</Markdown>
            </div>
            <span className={styles.actions}>
              <IconButton
                size="tiny"
                variant="text"
                aria-label={t('outreach.thread.copy')}
                onClick={(event: MouseEvent<HTMLButtonElement>) => {
                  void navigator.clipboard?.writeText(body).catch(() => {});
                  logPageClickBi({ page_name: page, button_name: 'copy_message' });
                  setRevealed(false);
                  // Drop focus, or `:focus-within` keeps the action showing after the copy.
                  event.currentTarget.blur();
                }}
              >
                <Copy size={14} aria-hidden />
              </IconButton>
            </span>
          </div>
          {time ? (
            <Typography variant="p3" color="secondary" className={styles.time}>
              <time dateTime={message.sentAt ?? undefined} title={exact}>
                {time}
              </time>
            </Typography>
          ) : null}
        </div>
        <MessageAttachments attachments={message.attachments} />
      </div>
    </div>
  );
}

/**
 * Every outreach thread with one contact as ONE chronological transcript — the contact page's "Full
 * conversation" (`contactTimelineRows`, contract `timeline` in the same fixture). Its bubbles are THIS file's
 * `TranscriptBubble`, so the timeline and a thread cannot disagree on who said what.
 *
 * A TOPIC row opens every switch of thread ("Lunch · Sep 8", with the thread's stage dot); pressing it opens
 * that one thread in its viewer, where its status, its decision and its composer live. The owner's lane is
 * not drawn here — it belongs to one thread and is read in that thread's viewer.
 */
export function ContactTimelineTranscript({
  threads,
  source,
  contactName,
  maestroAgentName,
  page,
  contactPhoto,
  avatarKey,
  onOpenThread,
}: {
  threads: TimelineThread[];
  /** The wire threads, index for index with `threads` — for each topic's stage and title fallback. */
  source: WebOutreachThread[];
  contactName: string;
  maestroAgentName: string;
  page: string;
  contactPhoto?: string;
  avatarKey: string;
  onOpenThread: (outreachId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const tr = t as unknown as TFn;
  const { logPageClickBi } = useGenericBi();
  const locale = i18n.language || undefined;
  const now = useMemo(() => new Date(), []);
  const ownerNames = useThreadOwnerNames();
  const rows = useMemo(() => contactTimelineRows(threads, contactName, now, undefined, ownerNames), [threads, contactName, now, ownerNames]);
  const deliveries = useMemo(() => threads.map((thread) => lastAgentDelivery(thread.messages as TranscriptMessage[])), [threads]);

  return (
    <div className={styles.transcript}>
      {rows.map((row, position) => {
        const divider = row.divider ? <DayDivider day={row.divider} /> : null;
        if ('topic' in row) {
          const { topic } = row;
          const thread = source[topic.thread];
          const stage = threadStage(thread ?? {});
          const title = renderTopicTitle(topic, thread ? threadTitle(thread, locale) : '', tr, locale);
          return (
            <div key={`t-${position}`} className={styles.messageBlock}>
              {divider}
              <Button
                variant="outlined"
                size="tiny"
                className={styles.topic}
                data-testid="contact-timeline-topic"
                title={title}
                startIcon={<StageDot stage={stage} />}
                endIcon={<ChevronRight size={16} className="dir-flip" aria-hidden />}
                onClick={() => {
                  logPageClickBi({ page_name: page, button_name: 'open_timeline_thread' }, { additionalFields: { stage: stageBiValue(stage) } });
                  onOpenThread(topic.outreachId);
                }}
              >
                {title}
              </Button>
            </div>
          );
        }
        const thread = threads[row.thread];
        const message = thread?.messages[row.index] as TranscriptMessage | undefined;
        if (!thread || !message) return null;
        const delivery = deliveries[row.thread];
        return (
          <TranscriptBubble
            key={`m-${row.thread}-${row.index}`}
            divider={divider}
            message={message}
            row={row}
            contactName={contactName}
            maestroAgentName={maestroAgentName}
            deliveryKey={delivery?.index === row.index ? delivery.key : undefined}
            page={page}
            contactPhoto={contactPhoto}
            avatarKey={avatarKey}
          />
        );
      })}
    </div>
  );
}

/** "Only you see this" — under the owner's own lane and the stage card, which the contact never sees. */
export function OwnerOnlyCaption() {
  const { t } = useTranslation();
  return (
    <span className={styles.ownerOnly}>
      <Lock size={12} aria-hidden />
      <Typography variant="p3" color="secondary">
        {t('outreach.thread.ownerOnly')}
      </Typography>
    </span>
  );
}
